<template>
  <div class="intercom-dropdown" ref="dropdownRef">
    <!-- Trigger -->
    <div @click="toggle" ref="triggerRef">
      <slot name="trigger" />
    </div>

    <!-- Dropdown Menu -->
    <Transition name="dropdown-fade">
      <div v-if="isOpen" class="intercom-dropdown-menu" :style="menuStyle">
        <slot />
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  /**
   * Dropdown alignment
   * @values left, right
   */
  align: {
    type: String,
    default: 'right',
    validator: (value) => ['left', 'right'].includes(value),
  },
  /**
   * Offset from trigger (px)
   */
  offset: {
    type: Number,
    default: 8,
  },
})

const emit = defineEmits(['open', 'close'])

const isOpen = ref(false)
const dropdownRef = ref(null)
const triggerRef = ref(null)

const menuStyle = computed(() => {
  return {
    [props.align]: '0',
    top: `calc(100% + ${props.offset}px)`,
  }
})

const toggle = () => {
  isOpen.value = !isOpen.value
  emit(isOpen.value ? 'open' : 'close')
}

const open = () => {
  isOpen.value = true
  emit('open')
}

const close = () => {
  isOpen.value = false
  emit('close')
}

// Click outside to close
const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    close()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

defineExpose({
  isOpen,
  toggle,
  open,
  close,
})
</script>

<style scoped>
/* ========================================
   INTERCOM DROPDOWN
   ======================================== */
.intercom-dropdown {
  position: relative;
  display: inline-block;
}

.intercom-dropdown-menu {
  position: absolute;
  min-width: 200px;
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  padding: var(--space-2);
  z-index: var(--z-dropdown);
}

/* ========================================
   DROPDOWN TRANSITION
   ======================================== */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity var(--transition-fast), transform var(--transition-fast);
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-fade-enter-to,
.dropdown-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
